import { useEffect, useState } from "react";
import { apiFetch } from "../api";
import { booting } from "../boot";
import { db } from "../db/client";

/*
 * Attachment bodies live in Cache Storage, keyed by content hash (/files/<sha256>), so copies of a
 * note share one cached file. They are fetched on view and prefetched in the background after
 * sync, so every note opens offline (v1: "offline on every device").
 * Files added on this device live in the local-files cache until uploaded.
 */
const FILES = "engram-files";
const LOCAL = "engram-local-files";
const urls = new Map<string, Promise<string | null>>();

export const fileKey = (sha: string) => `/files/${sha}`;

/** Cached bytes for a file, from this device's caches only. */
export async function cachedFile(sha: string): Promise<Response | undefined> {
  return (await (await caches.open(FILES)).match(fileKey(sha))) ?? (await (await caches.open(LOCAL)).match(`/local/${sha}`)) ?? undefined;
}

/** A file's bytes: this device's caches first, else the server (then cached). */
async function fileBlob(id: string, sha: string | undefined): Promise<Blob | null> {
  let res = sha ? await cachedFile(sha) : undefined;
  if (!res) {
    const net = await apiFetch(`/attachments/${id}`);
    if (!net.ok) return null;
    if (sha) await (await caches.open(FILES)).put(fileKey(sha), net.clone());
    res = net;
  }
  return res.blob();
}

async function load(id: string): Promise<string | null> {
  try {
    const row = await db.get<{ sha256: string }>("SELECT sha256 FROM attachments WHERE id = ?", [id]);
    const blob = await fileBlob(id, row?.sha256);
    return blob ? URL.createObjectURL(blob) : null;
  } catch {
    return null;
  }
}

/*
 * URLs already made, so a picture or card drawn again shows it in its first frame. Arriving a frame later made a
 * list restored to its scroll position drift (a card with a thumbnail is 11 px taller; features.mjs
 * list-keeps-its-place, chat 16).
 */
const madeFiles = new Map<string, string>();
const madeThumbs = new Map<string, string>();

export function attachmentUrl(id: string): Promise<string | null> {
  let p = urls.get(id);
  if (!p) {
    p = load(id);
    urls.set(id, p);
    void p.then((u) => (u == null ? urls.delete(id) : madeFiles.set(id, u))); // retry later if it failed (e.g. offline)
  }
  return booting(p);
}

function useMadeUrl(id: string | null | undefined, made: Map<string, string>, get: (id: string) => Promise<string | null>): string | null {
  const [loaded, setLoaded] = useState<{ url: string | null } | null>(null);
  useEffect(() => {
    if (!id || made.has(id)) return;
    let alive = true;
    void get(id).then((url) => alive && setLoaded({ url }));
    return () => {
      alive = false;
    };
  }, [id, made, get]);
  if (!id) return null;
  // While another id loads, the previous picture stays (as before), e.g. a sketch swapping in its new file.
  return made.get(id) ?? loaded?.url ?? null;
}

export function useAttachmentUrl(id: string | null | undefined): string | null {
  return useMadeUrl(id, madeFiles, attachmentUrl);
}

/*
 * Thumbnails for note lists and Home cards. A 72×64 card thumbnail used to show the photo itself: 16 cards of the
 * owner's phone photos decoded 46 megapixels (~180 MB of bitmaps). Each picture is now scaled once to THUMB_PX
 * wide (sharp in the widest card at 3× pixel density), kept in Cache Storage by content hash, and shown from there.
 * Small pictures, and anything the browser can't scale (SVG), show the file itself.
 */
const THUMBS = "engram-thumbs";
const THUMB_PX = 640;
const thumbKey = (sha: string) => `/thumbs/${sha}-${THUMB_PX}`;
const thumbUrls = new Map<string, Promise<string | null>>();
// Scaling decodes the full picture (~80 MB for a 20 MP photo): two at a time.
let scaling = 0;
const waiting: (() => void)[] = [];
async function oneOfTwo<T>(fn: () => Promise<T>): Promise<T> {
  if (scaling >= 2) await new Promise<void>((r) => waiting.push(r));
  scaling++;
  try {
    return await fn();
  } finally {
    scaling--;
    waiting.shift()?.();
  }
}

async function loadThumb(id: string): Promise<string | null> {
  try {
    const row = await db.get<{ sha256: string; width: number | null; height: number | null }>("SELECT sha256, width, height FROM attachments WHERE id = ?", [id]);
    const small = row?.width && row.height && row.width * row.height <= THUMB_PX * THUMB_PX;
    if (!row || small || typeof OffscreenCanvas === "undefined" || typeof caches === "undefined") return attachmentUrl(id);
    const cache = await caches.open(THUMBS);
    const hit = await cache.match(thumbKey(row.sha256));
    if (hit) return URL.createObjectURL(await hit.blob());
    const thumb = await oneOfTwo(async () => {
      const blob = await fileBlob(id, row.sha256);
      if (!blob) return null;
      const bmp = await createImageBitmap(blob, { resizeWidth: Math.min(THUMB_PX, row.width ?? THUMB_PX), resizeQuality: "high", imageOrientation: "from-image" });
      const canvas = new OffscreenCanvas(bmp.width, bmp.height);
      canvas.getContext("2d")!.drawImage(bmp, 0, 0);
      bmp.close();
      return canvas.convertToBlob({ type: "image/webp", quality: 0.82 });
    });
    if (!thumb) return null;
    await cache.put(thumbKey(row.sha256), new Response(thumb, { headers: { "content-type": thumb.type } }));
    return URL.createObjectURL(thumb);
  } catch {
    return attachmentUrl(id); // not scalable here (SVG, a damaged file): the file itself
  }
}

export function thumbnailUrl(id: string): Promise<string | null> {
  let p = thumbUrls.get(id);
  if (!p) {
    p = loadThumb(id);
    thumbUrls.set(id, p);
    void p.then((u) => (u == null ? thumbUrls.delete(id) : madeThumbs.set(id, u)));
  }
  return booting(p);
}

/** A picture attachment's thumbnail URL (lists, Home), or null while it loads or when there is none. */
export function useThumbnailUrl(id: string | null | undefined): string | null {
  return useMadeUrl(id, madeThumbs, thumbnailUrl);
}

export async function sha256Hex(blob: Blob): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, "0")).join("");
}

let prefetching = false;

/**
 * Downloads every attachment not cached yet, one at a time, and drops cached files no note uses any more.
 * Skipped on metered connections (Data Saver, or cellular where the browser says so).
 */
export async function prefetchAttachments(): Promise<void> {
  const conn = (navigator as Navigator & { connection?: { saveData?: boolean; type?: string } }).connection;
  if (prefetching || !navigator.onLine || conn?.saveData || conn?.type === "cellular" || typeof caches === "undefined") return;
  prefetching = true;
  try {
    const rows = await db.query<{ id: string; sha256: string }>(
      "SELECT MIN(a.id) AS id, a.sha256 FROM attachments a JOIN notes n ON n.id = a.note_id WHERE a.deleted = 0 AND n.deleted = 0 GROUP BY a.sha256",
    );
    const cache = await caches.open(FILES);
    const cached = new Set((await cache.keys()).map((r) => new URL(r.url).pathname));
    const live = new Set(rows.map((r) => fileKey(r.sha256)));
    for (const k of cached) if (!live.has(k)) await cache.delete(k);
    const thumbs = await caches.open(THUMBS);
    const liveShas = new Set(rows.map((r) => r.sha256));
    for (const req of await thumbs.keys()) {
      const name = new URL(req.url).pathname.slice("/thumbs/".length);
      if (!liveShas.has(name.split("-")[0]!) || !name.endsWith(`-${THUMB_PX}`)) await thumbs.delete(req);
    }
    const local = await caches.open(LOCAL);
    for (const r of rows) {
      if (cached.has(fileKey(r.sha256)) || (await local.match(`/local/${r.sha256}`))) continue;
      if (!navigator.onLine) break;
      const res = await apiFetch(`/attachments/${r.id}`);
      if (res.ok) await cache.put(fileKey(r.sha256), res);
    }
  } catch (e) {
    console.warn("attachment prefetch stopped", e);
  } finally {
    prefetching = false;
  }
}
